import type { ClientMessage } from '../../shared/protocol';

/**
 * v22 BOLT-108 — who owns the socket the moment it opens.
 *
 * The field bug: the client persists a resume token and auto-`rejoin`s with it
 * on EVERY open, before anything else. A still-live leftover token therefore
 * silently reattached a device to an OLD session as its OLD player instead of
 * joining the room the human just asked for — the "iPad didn't land on Team B"
 * report, and the reason QR/deep-link joins were inconsistent (the deep-link
 * join is exactly the path a stale token cancels).
 *
 * The rule, in one place: a DELIBERATE new-game action from Home (Create or
 * Join) always beats an automatic rejoin. `SocketClient.startFresh` drops the
 * token first, and this keeps the intent armed until a `session` frame proves
 * it landed, so a socket that is still mid-connect can't swallow it either.
 * The auto-rejoin survives untouched for what it was actually for: a genuine
 * reconnect (backgrounded app / network blip) where no Home action was taken.
 */
export type OpenAction =
  | { t: 'intent'; msg: ClientMessage }
  | { t: 'rejoin' }
  | { t: 'idle' };

/** Join failures that are pointless to retry — the intent dies with them. */
const TERMINAL_ERRORS = new Set(['no_room', 'room_full', 'protocol_version']);

export class ConnectIntent {
  private armed: ClientMessage | null = null;

  /** Arm a deliberate Create/Join. Clearing the resume token is the caller's
   *  job (`startFresh`) — this owns only the "did it land yet" question. */
  arm(msg: ClientMessage) {
    this.armed = msg;
  }
  get pending(): ClientMessage | null {
    return this.armed;
  }
  clear() {
    this.armed = null;
  }

  /** On socket open: an explicit intent ALWAYS wins over an auto-rejoin. */
  onOpen(hasToken: boolean): OpenAction {
    if (this.armed) return { t: 'intent', msg: this.armed };
    return hasToken ? { t: 'rejoin' } : { t: 'idle' };
  }

  /** A `session` frame = we're in the room we asked for; the intent is spent. */
  onSession() {
    this.armed = null;
  }

  /** `no_session` means a rejoin lost the race, so the intent may fire now.
   *  Terminal join errors kill it; anything else keeps it armed for the next
   *  open (a transient drop must not lose the player's tap). */
  onError(code: string | undefined): ClientMessage | null {
    if (!code) return null;
    if (TERMINAL_ERRORS.has(code)) {
      this.armed = null;
      return null;
    }
    return code === 'no_session' ? this.armed : null;
  }
}
